
import { Link, useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  Search,
  UserRound,
  X,
  Home,
  UtensilsCrossed,
  BookOpen,
  MapPin,
  ChevronRight,
  Wine,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import API from "../../services/api";
import logo from "../../assets/logo.png";
import "../../styles/navbar.css";

export default function Navbar() {
  const navigate = useNavigate();

  const [cartCount, setCartCount] = useState(0);
  const [loadingCart, setLoadingCart] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState("");

  // ========================================================
  // AUTENTICACIÓN
  // ========================================================

  const isAuthenticated = useCallback(() => {
    try {
      const auth = JSON.parse(
        localStorage.getItem("auth") || "null"
      );

      return Boolean(
        auth?.access ||
        auth?.access_token ||
        auth?.token
      );
    } catch {
      return false;
    }
  }, []);

  // ========================================================
  // CONTADOR DEL CARRITO LOCAL
  // ========================================================

  const getLocalCartCount = useCallback(() => {
    try {
      const savedCart = JSON.parse(
        localStorage.getItem("cart") || "[]"
      );

      if (!Array.isArray(savedCart)) return 0;

      return savedCart.reduce((total, item) => {
        const quantity = Number(
          item?.quantity ?? item?.qty ?? 1
        );

        return (
          total +
          (Number.isFinite(quantity)
            ? Math.max(1, quantity)
            : 1)
        );
      }, 0);
    } catch (error) {
      console.error(
        "Error leyendo el carrito local:",
        error
      );

      return 0;
    }
  }, []);

  // ========================================================
  // CARGAR CARRITO
  // ========================================================

  const fetchCart = useCallback(async () => {
    const localCount = getLocalCartCount();

    setCartCount(localCount);

    if (!isAuthenticated()) {
      setLoadingCart(false);
      return;
    }

    setLoadingCart(true);

    try {
      const response = await API.get("/cart/");

      const items =
        response.data?.items ??
        response.data?.data?.items ??
        [];

      if (Array.isArray(items) && localCount === 0) {
        const apiTotal = items.reduce((total, item) => {
          const quantity = Number(
            item?.quantity ?? item?.qty ?? 1
          );

          return (
            total +
            (Number.isFinite(quantity)
              ? Math.max(1, quantity)
              : 1)
          );
        }, 0);

        setCartCount(apiTotal);
      }
    } catch (error) {
      console.warn(
        "No fue posible cargar el carrito del servidor:",
        error
      );
    } finally {
      setLoadingCart(false);
    }
  }, [getLocalCartCount, isAuthenticated]);

  useEffect(() => {
    fetchCart();

    const handleCartUpdate = () => fetchCart();
    const handleStorage = () => fetchCart();

    window.addEventListener(
      "cartUpdated",
      handleCartUpdate
    );

    window.addEventListener(
      "storage",
      handleStorage
    );

    return () => {
      window.removeEventListener(
        "cartUpdated",
        handleCartUpdate
      );

      window.removeEventListener(
        "storage",
        handleStorage
      );
    };
  }, [fetchCart]);

  // ========================================================
  // CONTROL DEL MENÚ MÓVIL
  // ========================================================

  useEffect(() => {
    document.body.classList.toggle(
      "navbar-menu-open",
      menuOpen
    );

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.classList.remove(
        "navbar-menu-open"
      );

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen((open) => !open);
  };

  // ========================================================
  // BUSCADOR
  // ========================================================

  const handleSearch = (event) => {
    event.preventDefault();

    const value = search.trim();

    navigate(
      value
        ? `/catalog?search=${encodeURIComponent(value)}`
        : "/catalog"
    );

    setSearch("");
    closeMenu();
  };

  const handleMobileSearch = () => {
    closeMenu();
    navigate("/catalog");
  };

  // ========================================================
  // CARRITO
  // ========================================================

  const goToCart = () => {
    closeMenu();
    navigate("/cart");
  };

  // ========================================================
  // PERFIL
  // ========================================================

  const goToProfile = () => {
    closeMenu();

    if (!isAuthenticated()) {
      localStorage.setItem(
        "redirectAfterLogin",
        "/profile"
      );

      navigate("/login", {
        state: { from: "/profile" },
      });

      return;
    }

    navigate("/profile");
  };

  // ========================================================
  // ENLACES DE NAVEGACIÓN
  // ========================================================
// ========================================================
// ENLACES DE NAVEGACIÓN
// ========================================================

const navItems = [
  {
    to: "/",
    label: "Inicio",
    Icon: Home,
  },
  {
    to: "/carta-bar",
    label: "Menú",
    Icon: Wine,
  },
  {
    to: "/catalog",
    label: "Catálogo",
    Icon: UtensilsCrossed,
  },
  {
    to: "/blog",
    label: "Novedades",
    Icon: BookOpen,
  },
  {
    to: "/sedes",
    label: "Ubícanos",
    Icon: MapPin,
  },
];
  // ========================================================
  // TEMPLATE
  // ========================================================

  return (
    <>
      {/* ==================================================
          NAVBAR PRINCIPAL
      ================================================== */}

      <header className="navbar" role="banner">
        {/* LOGO Y MARCA */}

        <Link
          to="/"
          className="navbar-logo"
          onClick={closeMenu}
          aria-label="La Esquina del Parques - Inicio"
        >
          <img
            src={logo}
            alt="Logo de La Esquina del Parques"
          />

          <span className="navbar-brand-text">
            <strong>LA ESQUINA</strong>
            <small>DEL PARQUES</small>
          </span>
        </Link>

        {/* NAVEGACIÓN DE ESCRITORIO */}

        <nav
          className="navbar-nav"
          aria-label="Navegación principal"
        >
          {navItems.map(({ to, label, Icon }) => (
            <Link
              key={to}
              to={to}
              className="navbar-action navbar-nav-action"
              aria-label={label}
              data-tooltip={label}
              onClick={closeMenu}
            >
              <Icon size={21} aria-hidden="true" />

              <span className="navbar-nav-label">
                {label}
              </span>
            </Link>
          ))}
        </nav>

        {/* BUSCADOR DE ESCRITORIO */}

        <form
          className="navbar-search"
          onSubmit={handleSearch}
          role="search"
        >
          <Search
            size={19}
            className="navbar-search-icon"
            aria-hidden="true"
          />

          <input
            type="search"
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            placeholder="Buscar comidas, bebidas..."
            aria-label="Buscar comidas y bebidas"
          />

          <button
            type="submit"
            className="navbar-search-button"
            aria-label="Buscar"
            data-tooltip="Buscar"
          >
            <Search size={17} aria-hidden="true" />
          </button>
        </form>

        {/* ACCIONES */}

        <div className="navbar-actions">
          {/* BÚSQUEDA MÓVIL */}

          <button
            type="button"
            className="navbar-action navbar-mobile-search"
            onClick={handleMobileSearch}
            aria-label="Ir al menú"
            data-tooltip="Menú"
          >
            <Search size={21} aria-hidden="true" />
          </button>

          {/* CARRITO */}

          <button
            type="button"
            className="navbar-action navbar-desktop-action navbar-cart-action"
            onClick={goToCart}
            aria-label={`Carrito${
              cartCount > 0
                ? `, ${cartCount} productos`
                : ""
            }`}
            data-tooltip="Carrito"
          >
            <ShoppingCart
              size={21}
              aria-hidden="true"
            />

            {cartCount > 0 && (
              <span
                className="navbar-cart-badge"
                aria-live="polite"
              >
                {loadingCart ? "…" : cartCount}
              </span>
            )}
          </button>

          {/* CUENTA */}

          <button
            type="button"
            className="navbar-action navbar-desktop-action"
            onClick={goToProfile}
            aria-label="Mi cuenta"
            data-tooltip="Mi cuenta"
          >
            <UserRound
              size={21}
              aria-hidden="true"
            />
          </button>

          {/* HAMBURGUESA ANIMADA */}

          <button
            type="button"
            className={`navbar-hamburger ${
              menuOpen ? "active" : ""
            }`}
            onClick={toggleMenu}
            aria-label={
              menuOpen ? "Cerrar menú" : "Abrir menú"
            }
            aria-expanded={menuOpen}
            aria-controls="navbar-mobile-drawer"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      {/* ==================================================
          OVERLAY DEL MENÚ MÓVIL
      ================================================== */}

      <button
        type="button"
        className={`navbar-overlay ${
          menuOpen ? "active" : ""
        }`}
        onClick={closeMenu}
        aria-label="Cerrar menú de navegación"
        aria-hidden={!menuOpen}
        tabIndex={menuOpen ? 0 : -1}
      />

      {/* ==================================================
          DRAWER MÓVIL
      ================================================== */}

      <aside
        id="navbar-mobile-drawer"
        className={`navbar-drawer ${
          menuOpen ? "active" : ""
        }`}
        aria-hidden={!menuOpen}
        inert={!menuOpen}
      >
        {/* ENCABEZADO */}

        <div className="navbar-drawer-header">
          <Link
            to="/"
            className="navbar-drawer-logo"
            onClick={closeMenu}
            aria-label="Ir al inicio"
          >
            <img
              src={logo}
              alt="La Esquina del Parques"
            />

            <span className="navbar-brand-text">
              <strong>LA ESQUINA</strong>
              <small>DEL PARQUES</small>
            </span>
          </Link>

          <button
            type="button"
            className="navbar-drawer-close"
            onClick={closeMenu}
            aria-label="Cerrar menú"
          >
            <X size={25} aria-hidden="true" />
          </button>
        </div>

        <div className="navbar-drawer-line" />

        {/* PRESENTACIÓN */}

        <div className="navbar-drawer-welcome">
          <span className="navbar-drawer-eyebrow">
            BIENVENIDO
          </span>

          <h2>Tu esquina favorita</h2>

          <p>
            Buenos sabores, bebidas y buenos momentos.
          </p>
        </div>

        {/* BUSCADOR MÓVIL */}

        <form
          className="navbar-mobile-search-form"
          onSubmit={handleSearch}
          role="search"
        >
          <Search
            size={18}
            aria-hidden="true"
          />

          <input
            type="search"
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            placeholder="¿Qué se te antoja?"
            aria-label="Buscar en el menú"
          />

          <button
            type="submit"
            aria-label="Buscar"
          >
            <ChevronRight
              size={19}
              aria-hidden="true"
            />
          </button>
        </form>

        {/* ENLACES MÓVILES */}

        <nav
          className="navbar-mobile-nav"
          aria-label="Navegación móvil"
        >
          {navItems.map(({ to, label, Icon }) => (
            <Link
              key={to}
              to={to}
              className="navbar-mobile-link"
              onClick={closeMenu}
            >
              <span className="navbar-mobile-icon">
                <Icon
                  size={20}
                  aria-hidden="true"
                />
              </span>

              <span className="navbar-mobile-label">
                {label}
              </span>

              <ChevronRight
                size={18}
                className="navbar-mobile-chevron"
                aria-hidden="true"
              />
            </Link>
          ))}

          {/* CARRITO MÓVIL */}

          <button
            type="button"
            className="navbar-mobile-link"
            onClick={goToCart}
          >
            <span className="navbar-mobile-icon">
              <ShoppingCart
                size={20}
                aria-hidden="true"
              />
            </span>

            <span className="navbar-mobile-label">
              Mi carrito
            </span>

            {cartCount > 0 && (
              <span
                className="navbar-mobile-count"
                aria-live="polite"
              >
                {loadingCart ? "…" : cartCount}
              </span>
            )}

            <ChevronRight
              size={18}
              className="navbar-mobile-chevron"
              aria-hidden="true"
            />
          </button>

          {/* CUENTA MÓVIL */}

          <button
            type="button"
            className="navbar-mobile-link"
            onClick={goToProfile}
          >
            <span className="navbar-mobile-icon">
              <UserRound
                size={20}
                aria-hidden="true"
              />
            </span>

            <span className="navbar-mobile-label">
              Mi cuenta
            </span>

            <ChevronRight
              size={18}
              className="navbar-mobile-chevron"
              aria-hidden="true"
            />
          </button>
        </nav>

        {/* PIE DEL DRAWER */}

        <div className="navbar-drawer-footer">
          <span className="navbar-drawer-footer-icon">
            <Wine size={19} aria-hidden="true" />
          </span>

          <div className="navbar-drawer-footer-copy">
            <strong>LA ESQUINA DEL PARQUES</strong>

            <span>
              BUEN SABOR · BUENOS MOMENTOS
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}
